import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, KeyRound, Megaphone, Mail, BarChart3 } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { CONTACT as DEFAULT_CONTACT } from "../../data/site";

const CONTACT_FIELDS = [
  { key: "phone", label: "Phone number" },
  { key: "email", label: "Email address" },
  { key: "address", label: "Address" },
  { key: "hours", label: "Opening hours" },
  { key: "safeguarding", label: "Safeguarding phone" },
  { key: "safeguardingHours", label: "Safeguarding hours" },
  { key: "safeguardingOOH", label: "Out-of-hours safeguarding" },
];

function ContactCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/site").then((r) => setForm({ ...DEFAULT_CONTACT, ...(r.data || {}) })).catch(() => setForm(DEFAULT_CONTACT));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/site", form);
      toast.success("Contact details updated across the website.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-stone-500" />;

  return (
    <form onSubmit={save} className="rounded-2xl bg-white border border-stone-200 p-5 space-y-4" data-testid="settings-contact-card">
      <h3 className="font-heading text-lg font-semibold text-stone-900">Contact details & opening hours</h3>
      <p className="text-sm text-stone-500">These appear in the footer and across the Contact, Careers, Safeguarding and policy pages.</p>
      {CONTACT_FIELDS.map((f) => (
        <div key={f.key}>
          <label htmlFor={`contact-${f.key}`} className="block text-base font-medium text-stone-900 mb-1">{f.label}</label>
          <input id={`contact-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid={`settings-contact-${f.key}`} />
        </div>
      ))}
      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="settings-contact-save">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save contact details
      </button>
    </form>
  );
}

function NoticeCard() {
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/notice").then((r) => setNotice(r.data)).catch(() => setNotice({ enabled: false, text: "" }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/notice", notice);
      toast.success(notice.enabled ? "Notice is now showing on the website." : "Notice saved (currently off).");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!notice) return <Loader2 className="h-5 w-5 animate-spin text-stone-500" />;

  return (
    <form onSubmit={save} className="rounded-2xl bg-white border border-stone-200 p-5 space-y-4" data-testid="settings-notice-card">
      <h3 className="font-heading text-lg font-semibold text-stone-900 flex items-center gap-2">
        <Megaphone className="h-5 w-5 text-amber-700" /> Site notice banner
      </h3>
      <p className="text-sm text-stone-500">Great for closure notices (e.g. bank holidays) or upcoming events. Shows at the top of every page.</p>
      <div>
        <label htmlFor="notice-text" className="block text-base font-medium text-stone-900 mb-1">Notice text</label>
        <textarea id="notice-text" rows={3} value={notice.text} onChange={(e) => setNotice({ ...notice, text: e.target.value })} placeholder="e.g. We are closed on Monday 31 August for the bank holiday." className="w-full rounded-xl border-2 border-stone-300 px-3 py-2 focus-visible:border-orange-600" data-testid="settings-notice-text" />
      </div>
      <label className="flex items-center gap-2 text-base text-stone-800">
        <input type="checkbox" checked={notice.enabled} onChange={(e) => setNotice({ ...notice, enabled: e.target.checked })} className="h-5 w-5 accent-orange-600" data-testid="settings-notice-enabled" />
        Show this notice on the website
      </label>
      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="settings-notice-save">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save notice
      </button>
    </form>
  );
}

function NotificationsCard() {
  const [emails, setEmails] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/notifications").then((r) => setEmails(r.data.emails)).catch(() => setEmails([]));
  }, []);

  const save = async (list) => {
    setSaving(true);
    try {
      await api.put("/admin/settings/notifications", { emails: list });
      setEmails(list);
      toast.success("Notification recipients updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    const email = newEmail.toLowerCase().trim();
    if (!email) return;
    if (emails.includes(email)) return toast.error("That email is already on the list.");
    save([...emails, email]);
    setNewEmail("");
  };

  if (emails === null) return <Loader2 className="h-5 w-5 animate-spin text-stone-500" />;

  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5 space-y-4" data-testid="settings-notifications-card">
      <h3 className="font-heading text-lg font-semibold text-stone-900 flex items-center gap-2">
        <Mail className="h-5 w-5 text-orange-700" /> Enquiry email notifications
      </h3>
      <p className="text-sm text-stone-500">When a new enquiry arrives, an email is sent to everyone on this list.</p>
      <ul className="space-y-2">
        {emails.map((em) => (
          <li key={em} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2" data-testid={`notify-email-${em}`}>
            <span className="text-base text-stone-800">{em}</span>
            <button
              onClick={() => (emails.length > 1 ? save(emails.filter((x) => x !== em)) : toast.error("At least one email must stay on the list."))}
              disabled={saving}
              className="rounded-lg p-1.5 text-stone-500 hover:bg-red-50 hover:text-red-700"
              aria-label={`Remove ${em}`}
              data-testid={`notify-email-remove-${em}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="flex gap-2">
        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Add an email address" aria-label="Add notification email" className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="notify-email-input" />
        <button type="submit" disabled={saving} className="inline-flex h-11 items-center gap-1 px-4 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="notify-email-add">
          <Plus className="h-5 w-5" /> Add
        </button>
      </form>
    </div>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return toast.error("The new passwords do not match.");
    if (form.next.length < 8) return toast.error("New password must be at least 8 characters.");
    setSaving(true);
    try {
      const res = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success(res.data?.message || "Password updated.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="rounded-2xl bg-white border border-stone-200 p-5 space-y-4" data-testid="settings-password-card">
      <h3 className="font-heading text-lg font-semibold text-stone-900 flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-orange-700" /> Change your password
      </h3>
      <div>
        <label htmlFor="pw-current" className="block text-base font-medium text-stone-900 mb-1">Current password</label>
        <input id="pw-current" type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} required autoComplete="current-password" className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="password-current-input" />
      </div>
      <div>
        <label htmlFor="pw-new" className="block text-base font-medium text-stone-900 mb-1">New password (min 8 characters)</label>
        <input id="pw-new" type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} required minLength={8} autoComplete="new-password" className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="password-new-input" />
      </div>
      <div>
        <label htmlFor="pw-confirm" className="block text-base font-medium text-stone-900 mb-1">Confirm new password</label>
        <input id="pw-confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={8} autoComplete="new-password" className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="password-confirm-input" />
      </div>
      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="password-save-button">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <KeyRound className="h-5 w-5" />} Update password
      </button>
      <p className="text-sm text-stone-500">You'll use the new password the next time you sign in.</p>
    </form>
  );
}

function StatsCard() {
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/stats").then((r) => setStats(r.data.stats)).catch(() => setStats(null));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/stats", { stats });
      toast.success("Homepage numbers updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!stats) return <Loader2 className="h-5 w-5 animate-spin text-stone-500" />;

  return (
    <form onSubmit={save} className="rounded-2xl bg-white border border-stone-200 p-5 space-y-4" data-testid="settings-stats-card">
      <h3 className="font-heading text-lg font-semibold text-stone-900 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-orange-700" /> Homepage "in numbers" strip
      </h3>
      <p className="text-sm text-stone-500">The four animated stats on the homepage. Numbers count up automatically; text shows as-is.</p>
      {stats.map((s, i) => (
        <div key={i} className="grid grid-cols-[6rem_4rem_1fr] gap-2 items-end">
          <div>
            <label htmlFor={`stat-value-${i}`} className="block text-sm font-medium text-stone-700 mb-1">Value</label>
            <input id={`stat-value-${i}`} value={s.value} onChange={(e) => setStats(stats.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} className="h-11 w-full rounded-xl border-2 border-stone-300 px-2 focus-visible:border-orange-600" data-testid={`stat-value-input-${i}`} />
          </div>
          <div>
            <label htmlFor={`stat-suffix-${i}`} className="block text-sm font-medium text-stone-700 mb-1">Suffix</label>
            <input id={`stat-suffix-${i}`} value={s.suffix} onChange={(e) => setStats(stats.map((x, j) => (j === i ? { ...x, suffix: e.target.value } : x)))} placeholder="%" className="h-11 w-full rounded-xl border-2 border-stone-300 px-2 focus-visible:border-orange-600" data-testid={`stat-suffix-input-${i}`} />
          </div>
          <div>
            <label htmlFor={`stat-label-${i}`} className="block text-sm font-medium text-stone-700 mb-1">Label</label>
            <input id={`stat-label-${i}`} value={s.label} onChange={(e) => setStats(stats.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid={`stat-label-input-${i}`} />
          </div>
        </div>
      ))}
      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="settings-stats-save">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save numbers
      </button>
    </form>
  );
}

export default function SettingsTab() {
  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="settings-tab">
      <div className="space-y-8">
        <ContactCard />
        <StatsCard />
      </div>
      <div className="space-y-8">
        <NoticeCard />
        <NotificationsCard />
        <PasswordCard />
      </div>
    </div>
  );
}
